import React, { useCallback } from 'react';
import { ClientInformationOrder } from '@/modules/order/components/ClientInformationOrder/ClientInformationOrder.tsx';
import { OrderSummary } from '@/modules/order/components/OrderSummary/OrderSummary.tsx';
import { useOrderContext } from '@/modules/order/context/OrderContext.tsx';
import { OrderProductSelection } from '@/modules/order/components/ProductSelection/OrderProductSelection.tsx';
import { ClientInformationSummary } from '@/shared/components/ClientInformationSummary.tsx';
import { OrderProductSelectionSummary } from '../ProductSelection/OrderProductSelectionSummary';
import { useTranslation } from 'react-i18next';
import {
    Wizard,
    WizardClientSection,
    WizardSection,
    WizardSections,
    WizardSidebar,
    WizardSidebarSections,
} from '@/shared/components/Wizard';
import { useSections } from '@/shared/hooks/useSections.ts';
import { useBalanceSheetContext } from '@/shared/components/ProductGroupHeader/context/BalanceSheetContext';
import { BalanceSheet } from '@/shared/components/ProductGroupHeader/BalanceSheet';
import { FrancoIndicator } from '@/shared/components/ProductGroupHeader/components/FrancoIndicator';
import { MessageBox } from '@schuettflix/react-components';
import { useMarketContext } from '@/shared/context/MarketContext';
import { SectionSummary } from '@/shared/components/SectionSummary.tsx';

export const OrderWizard: React.FC = () => {
    const { t } = useTranslation();
    const { contextData, position } = useOrderContext();
    const { marketCode } = useMarketContext();
    const { balanceProducts } = useBalanceSheetContext();
    const { isCurrentSection, goToNextSection, goToSection } = useSections({
        sections: ['client-information', 'product-selection', 'summary'],
        initialSection: 'client-information',
    });

    const customerInfo = contextData.getCustomerInformation();
    const businessCase = contextData.getBusinessCase();

    // The following callback functions remove the order summary data from the cache whenever a user clicks the edit icon.
    // This solution does not take into account that if the user makes no changes that no new data is needed.
    // But the current implementation of the wizard would require a bigger refactoring in order to optimize this solution.

    const handleClientInformationEdit = useCallback(() => {
        goToSection('client-information');
    }, [goToSection]);

    const handleProductSelectionEdit = useCallback(() => {
        goToSection('product-selection');
    }, [goToSection]);

    return (
        <Wizard>
            <WizardSections>
                {isCurrentSection('client-information') && (
                    <WizardClientSection title={`1. ${t('sections.clientInformation.title')}`}>
                        <ClientInformationOrder onContinue={goToNextSection} />
                    </WizardClientSection>
                )}

                {isCurrentSection('product-selection') && (
                    <WizardSection
                        title={`2. ${t('sections.productGroupSelection.title')}`}
                        headerAside={
                            balanceProducts.length ? (
                                <FrancoIndicator
                                    products={balanceProducts}
                                    productGroupType={businessCase?.pricingStrategy}
                                />
                            ) : undefined
                        }
                    >
                        <OrderProductSelection onContinue={goToNextSection} />
                    </WizardSection>
                )}

                {isCurrentSection('summary') && (
                    <WizardSection title={`3. ${t('sections.summary.title')}`}>
                        <OrderSummary />
                    </WizardSection>
                )}
            </WizardSections>
            {!isCurrentSection('client-information') && (
                <WizardSidebar>
                    {balanceProducts.length && businessCase ? (
                        <BalanceSheet productGroupType={businessCase.pricingStrategy} products={balanceProducts} />
                    ) : null}
                    {marketCode === 'DE' && (
                        <MessageBox type="warning">
                            {t(`sections.productGroupSelection.legalCertificateHint`)}
                        </MessageBox>
                    )}
                    <WizardSidebarSections>
                        {customerInfo && (
                            <SectionSummary
                                title={`1. ${t('sections.clientInformation.title')}`}
                                onEditClick={handleClientInformationEdit}
                                editable
                            >
                                <ClientInformationSummary
                                    organizationId={customerInfo.organizationId}
                                    constructionProjectId={customerInfo.constructionProjectId}
                                    manualCostCenter={customerInfo.manualCostCenter}
                                    sourceCostCenter={position?.customer.manualCostCenter}
                                />
                            </SectionSummary>
                        )}
                        {!!businessCase && isCurrentSection('summary') && (
                            <SectionSummary
                                title={`2. ${t('sections.productGroupSelection.title')}`}
                                onEditClick={handleProductSelectionEdit}
                                editable
                            >
                                <OrderProductSelectionSummary />
                            </SectionSummary>
                        )}
                    </WizardSidebarSections>
                </WizardSidebar>
            )}
        </Wizard>
    );
};
